import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useToast } from '@/hooks/use-toast';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { API_CONFIG } from '@/constants/env';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Briefcase, MapPin } from 'lucide-react';
import { NavBar } from '@/components/navbar';
import { Footer } from '@/components/SiteFooter';
import { trackEvent } from '@/utils/analytics';
import { collectBrowserInfo } from '@/utils/browserInfo';
import { SuccessModal } from '@/components/SuccessModal';

const formSchema = (t: (key: string) => string) =>
  z.object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    email: z.string().email('Invalid email address'),
    phone: z.string().min(1, 'Phone number is required'),
    linkedIn: z.string().min(1, 'LinkedIn profile is required'),
    currentCompany: z.string().optional(),
    coverLetter: z
      .string()
      .min(100, 'Please provide at least 100 characters for your cover letter'),
    cvText: z.string().min(50, 'Please provide at least 50 characters of your CV/resume'),
    portfolio: z.string().optional(),
  });

// Use a type cast to make TypeScript happy with our parameterized schema
type FormData = z.infer<ReturnType<typeof formSchema>>;

interface JobPosition {
  title: string;
  location: string;
  type: string;
  salary?: string;
  description: string;
  requirements: string[];
  slug: string;
}

const ApplyNow = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { roleId } = useParams();
  const { t } = useTranslation('common');

  // Get all job positions from translations
  const jobPositions =
    (t('careers.openingsSection.jobPositions', { returnObjects: true }) as JobPosition[]) || [];

  // Find the current role by slug
  const currentRole = jobPositions.find(job => job.slug === roleId);

  // Check for "general" application
  const isGeneralApplication = roleId === 'general';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLocalhost, setIsLocalhost] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Create the validation schema with translations
  const validationSchema = formSchema(t);

  const form = useForm<FormData>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      linkedIn: '',
      currentCompany: '',
      coverLetter: '',
      cvText: '',
      portfolio: '',
    },
  });

  useEffect(() => {
    // Check if running on localhost
    const hostname = window.location.hostname;
    setIsLocalhost(hostname === 'localhost' || hostname === '127.0.0.1');
  }, []);

  // Set fake data for development environment
  useEffect(() => {
    if (isLocalhost) {
      form.reset({
        firstName: 'John',
        lastName: 'Doe',
        email: 'john.doe@example.com',
        phone: '+1 (555) 123-4567',
        linkedIn: 'https://linkedin.com/in/johndoe',
        currentCompany: 'Current Company Inc.',
        coverLetter: `I am excited to apply for the ${isGeneralApplication ? 'position at Verdea' : currentRole?.title} position. I have extensive experience in compliance and regulatory technology, and I believe my skills align perfectly with your requirements.`,
        cvText:
          'Software Engineer with 5+ years of experience building scalable web applications using React, TypeScript, and Python. Strong background in compliance technology and AI integration.',
        portfolio: 'https://github.com/johndoe',
      });
    }
  }, [isLocalhost, form, isGeneralApplication, currentRole]);

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);

    // Collect browser and device information
    const browserInfo = collectBrowserInfo();

    // Prepare the message combining application details
    const roleTitle = isGeneralApplication
      ? 'General Application'
      : currentRole?.title || 'Unknown Role';
    const roleDetails =
      !isGeneralApplication && currentRole
        ? `\n\nRole Details:\n- Title: ${currentRole.title}\n- Location: ${currentRole.location}\n- Type: ${currentRole.type}${currentRole.salary ? `\n- Salary: ${currentRole.salary}` : ''}`
        : '';

    const message = `Job Application: ${roleTitle}\n\nCover Letter:\n${data.coverLetter}\n\nCV/Resume:\n${data.cvText}\n\nContact Information:\n- LinkedIn: ${data.linkedIn}\n- Phone: ${data.phone}${data.portfolio ? `\n- Portfolio: ${data.portfolio}` : ''}${roleDetails}`;

    // Prepare submission data for contact endpoint
    const submissionData = {
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.email,
      company: data.currentCompany || 'Not provided',
      subject: `Job Application: ${roleTitle}`,
      message: message,
      compliance_process: `Job Application for ${roleTitle} (Slug: ${roleId})`,
      request_demo: 'no',
      browser_info: browserInfo,
    };

    // Track the form submission attempt
    trackEvent('JobApplication', 'FormSubmit', roleTitle);

    // Timing start
    const startTime = performance.now();

    try {
      const response = await fetch(`${API_CONFIG.API_BASE_URL}/contact/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      if (!response.ok) {
        // Handle non-200 responses without trying to parse them as JSON
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      // Check if the response has content before trying to parse it
      const contentType = response.headers.get('content-type');
      let responseData;

      if (contentType && contentType.includes('application/json') && response.bodyUsed === false) {
        // Only try to parse JSON if the content type is application/json and body hasn't been read
        const text = await response.text();
        responseData = text ? JSON.parse(text) : {};
      }

      // Track successful form submission
      trackEvent('JobApplication', 'FormSubmitSuccess', roleTitle);

      setIsSubmitting(false);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error submitting application:', error);
      let errorMessage = 'There was an error submitting your application. Please try again later.';

      if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast({
        title: 'Submission Failed',
        description: errorMessage,
        variant: 'destructive',
      });

      // Track form submission error
      trackEvent('JobApplication', 'FormSubmitError', errorMessage);

      setIsSubmitting(false);
    } finally {
      // Track timing for form submission
      const endTime = performance.now();
      const timeTaken = Math.round(endTime - startTime);
      // Note: trackTiming function might not exist, we'll use trackEvent
      trackEvent('JobApplication', 'FormSubmissionTime', `${timeTaken}ms`);
    }
  };

  if (!isGeneralApplication && !currentRole) {
    navigate('/careers');
    return null;
  }

  const pageTitle = isGeneralApplication
    ? t('careers.cta.buttonPrimary', 'General Application')
    : currentRole?.title || 'Apply Now';

  const pageDescription = isGeneralApplication
    ? "Submit a general application if you don't see a specific role that matches your skills."
    : currentRole?.description;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <NavBar />
      <main className="container mx-auto px-4 pt-24 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Job Details Section - Show first for specific roles */}
          {!isGeneralApplication && currentRole && (
            <Card className="max-w-3xl mx-auto mb-8">
              <CardHeader>
                <CardTitle className="text-3xl font-bold">{currentRole.title}</CardTitle>
                <div className="mt-4 space-y-3">
                  <div className="text-gray-800 whitespace-pre-line leading-relaxed space-y-3 break-words">
                    {currentRole.description.split('\n\n').map((section, index) => {
                      const trimmed = section.trim();
                      if (!trimmed) return null;

                      // Handle separator lines (***)
                      if (trimmed === '***') {
                        return <hr key={index} className="my-6 border-gray-300" />;
                      }

                      const lines = trimmed.split('\n');
                      const firstLine = lines[0];
                      const isHeading =
                        firstLine &&
                        (firstLine.startsWith('The ') ||
                          firstLine.startsWith('Der ') ||
                          firstLine.startsWith('Die ') ||
                          firstLine.startsWith('Das ') ||
                          firstLine.endsWith(':'));

                      if (isHeading && lines.length > 1) {
                        // Strip markdown ** from heading
                        const cleanHeading = firstLine.replace(/\*\*/g, '');
                        return (
                          <div key={index} className="space-y-1">
                            <h4 className="text-lg font-semibold text-gray-900">{cleanHeading}</h4>
                            <div className="space-y-1">
                              {lines.slice(1).map((line, lineIndex) => (
                                <div key={lineIndex} className="text-gray-700">
                                  {line.includes(':') ? (
                                    <div className="flex">
                                      <span className="font-medium min-w-[140px] flex-shrink-0">
                                        {line.substring(0, line.indexOf(':')).trim()}:
                                      </span>
                                      <span className="ml-2 break-words">
                                        {line.substring(line.indexOf(':') + 1).trim()}
                                      </span>
                                    </div>
                                  ) : (
                                    line
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }

                      if (isHeading && lines.length === 1) {
                        // Strip markdown ** from heading
                        const cleanHeading = firstLine.replace(/\*\*/g, '');
                        return (
                          <h4 key={index} className="text-lg font-semibold text-gray-900">
                            {cleanHeading}
                          </h4>
                        );
                      }

                      return (
                        <div key={index} className="text-gray-700 break-words">
                          {lines.map((line, lineIndex) => {
                            // Strip markdown ** and * from regular text lines
                            const cleanLine = line.replace(/\*\*/g, '').replace(/\*(.*?)\*/g, '$1');
                            return (
                              <div key={lineIndex}>
                                {line.includes(':') ? (
                                  <div className="flex">
                                    <span className="font-medium min-w-[140px] flex-shrink-0">
                                      {line.substring(0, line.indexOf(':')).trim()}:
                                    </span>
                                    <span className="ml-2 break-words">
                                      {line.substring(line.indexOf(':') + 1).trim()}
                                    </span>
                                  </div>
                                ) : (
                                  cleanLine
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex flex-wrap gap-4 pt-2">
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                      {currentRole.location}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Briefcase className="h-4 w-4 mr-1 text-gray-400" />
                      {currentRole.type}
                    </div>
                    {currentRole.salary && (
                      <div className="flex items-center text-sm text-gray-600">
                        {currentRole.salary}
                      </div>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Requirements Section */}
                {currentRole.requirements && currentRole.requirements.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-xl font-semibold mb-4">Role Requirements</h3>
                    <div className="space-y-4">
                      {(() => {
                        const categories: Array<{ title: string; items: string[] }> = [];
                        let currentCategory: { title: string; items: string[] } | null = null;
                        const categoryTitles = [
                          'Must-Have',
                          'Strong Advantage',
                          'Nice-to-Have',
                          'Erforderlich',
                          'Starker Vorteil',
                          'Wünschenswert',
                        ];

                        for (const req of currentRole.requirements) {
                          if (categoryTitles.includes(req)) {
                            if (currentCategory) categories.push(currentCategory);
                            currentCategory = { title: req, items: [] };
                          } else {
                            if (!currentCategory)
                              currentCategory = { title: 'Requirements', items: [] };
                            currentCategory.items.push(req);
                          }
                        }
                        if (currentCategory) categories.push(currentCategory);

                        return categories.map((category, catIndex) => (
                          <div key={catIndex} className="space-y-2">
                            <h4 className="text-lg font-semibold text-gray-900">
                              {category.title}
                            </h4>
                            <ul className="space-y-2">
                              {category.items.map((item, itemIndex) => (
                                <li key={itemIndex} className="flex items-start">
                                  <div className="h-1.5 w-1.5 bg-[#229302] rounded-full mr-2 mt-2 flex-shrink-0"></div>
                                  <span className="text-gray-700 break-words">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ));
                      })()}
                    </div>
                    <p className="text-sm text-gray-500 mt-4">
                      Don't meet all requirements? We still encourage you to apply if you're
                      passionate about the role!
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* General Application Info */}
          {isGeneralApplication && (
            <Card className="max-w-3xl mx-auto mb-8">
              <CardHeader>
                <CardTitle className="text-3xl font-bold">{pageTitle}</CardTitle>
                <p className="text-gray-600 mt-2 whitespace-pre-line">{pageDescription}</p>
              </CardHeader>
            </Card>
          )}

          {/* Application Form - Always at the bottom */}
          <Card className="max-w-3xl mx-auto">
            <CardHeader>
              <CardTitle className="text-2xl font-bold">
                {isGeneralApplication ? 'General Application Form' : 'Application Form'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className={`space-y-6 ${showSuccessModal ? 'pointer-events-none opacity-75' : ''}`}
                >
                  <div className="space-y-2">
                    <FormLabel>First Name *</FormLabel>
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input placeholder="John" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <FormLabel>Last Name *</FormLabel>
                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input placeholder="Doe" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <FormLabel>Email Address *</FormLabel>
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input type="email" placeholder="john@example.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <FormLabel>Phone Number *</FormLabel>
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input placeholder="+1 (555) 123-4567" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <FormLabel>LinkedIn Profile *</FormLabel>
                    <FormField
                      control={form.control}
                      name="linkedIn"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input placeholder="https://linkedin.com/in/username" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <FormLabel>Current Company (Optional)</FormLabel>
                    <FormField
                      control={form.control}
                      name="currentCompany"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input placeholder="Your current company" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <FormLabel>Portfolio/GitHub (Optional)</FormLabel>
                    <FormField
                      control={form.control}
                      name="portfolio"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              placeholder="https://github.com/username or https://portfolio.example.com"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <FormLabel>CV/Resume Text *</FormLabel>
                    <FormField
                      control={form.control}
                      name="cvText"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Textarea
                              className="min-h-[200px] font-mono text-sm"
                              placeholder="Paste your CV/resume here. Include your work experience, education, skills, and any relevant projects. Please do not include sensitive personal information."
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <p className="text-sm text-gray-500">
                      Please paste your CV/resume as text. We prefer text over file uploads for
                      better processing.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <FormLabel>Cover Letter *</FormLabel>
                    <FormField
                      control={form.control}
                      name="coverLetter"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Textarea
                              className="min-h-[150px]"
                              placeholder={`Why are you interested in ${isGeneralApplication ? 'working at Verdea' : 'this position'}? What relevant experience do you have?`}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#184c06] hover:bg-[#229302]"
                    disabled={isSubmitting || showSuccessModal}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Application'}
                  </Button>

                  <p className="text-sm text-gray-500 text-center">
                    By submitting this application, you agree to our{' '}
                    <a href="/privacy" className="text-[#229302] hover:underline">
                      Privacy Policy
                    </a>
                    . We'll contact you via email or phone regarding your application.
                  </p>
                </form>
              </Form>

              {/* Success Modal */}
              <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => {
                  setShowSuccessModal(false);
                  form.reset();
                  navigate('/careers');
                }}
                title="Application Submitted"
                description="Thank you for applying! We'll review your application and be in touch soon."
              />
            </CardContent>
          </Card>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
};

export default ApplyNow;
